<template>
    <div>
        <strong>Message Details</strong>

        <div v-if="template_context === 'order-placed'">
            <ul>
                <li>RedChirp should send this message when an order is placed in your connected POS / CRM system</li>
                <li>How this message is sent depends on if you have other "Order Placed" messages enabled:
                    <ul>
                        <li>If only this message is enabled then it should be the only message utilized for all orders</li>
                        <li>If you have multiple "Order Placed" message types enabled, please see the <a href="#" @click.prevent="$emit('scroll-to-faq', 'orderPlacedMultipleFaq')">multiple "Order Placed" FAQ</a> for a more detailed explanation</li>            
                    </ul>
                </li>
                <li>RedChirp should trigger this message when an order is placed</li>
                <ul>
                    <li><strong>Please note:</strong> this message is triggered when an order is <strong>placed</strong></li>
                    <li>Importantly, orders are placed <strong>before</strong> they are tendered (paid)</li>
                    <li>Keep in mind that an order may be placed and then the payment method may, for example, decline</li>
                </ul>
                <li>This message should be triggered for all order types</li>
                <ul>
                    <li>Keep this in mind when considering this message for club shipments; if needed, you can use conditions to exclude or change the text for club orders</li>
                    <li>Keep this in mind if you are creating orders for reservations / events; if needed, you can use conditions to exclude or change the text in this circumstance</li>
                </ul>
            </ul>
        </div>
        
        <div v-if="template_context === 'order-placed-request-opt-in'">
            <ul style="margin-top: 10px; margin-bottom: 0;">
                <li>RedChirp should send this message when an order is placed in your connected POS / CRM system</li>
                <ul>
                    <li>By default, this includes club orders</li>
                    <li>You can change this behavior by creating a conditional message</li>
                </ul>
                <li>How this message is sent depends on if you have other "Order Placed" messages enabled:
                    <ul>
                        <li>If only this message is enabled then it should be the only message utilized for all orders</li>
                        <li>If you have multiple "Order Placed" message types enabled, please see the <a href="#" @click.prevent="$emit('scroll-to-faq', 'orderPlacedMultipleFaq')">multiple "Order Placed" FAQ</a> for a more detailed explanation</li>            
                    </ul>
                </li>
                <li>Sending this message starts a double opt-in flow:
                    <ul>
                        <li>Initially, automated processes should opt the recipient out of promotional messaging until they reply YES</li>
                    </ul>
                </li>
                <li>RedChirp should attempt to avoid sending this message when:
                    <ul>
                        <li>The phone number is not the bill to phone number for the order</li>
                        <li>The order total is less than $1.00</li>
                        <li>The recipient already has promotional consent</li>
                        <li>The phone number has received this message type within the last 7 days</li>
                    </ul>
                </li>
                <li>
                    We recommend against adding any promotional, incentive, or marketing language to this message
                </li>
            </ul>
        </div>

        <div v-if="template_context === 'order-placed-request-order-confirmation'">
            <ul>
                <li>RedChirp should send this message when an order is placed in your connected POS / CRM system</li>
                <li>RedChirp will attempt to choose the right message when multiple "Order Placed" message types are enabled:
                    <ul>
                        <li>In particular, when enabled, this "Order Placed: Request Confirmation and Opt-In (Uses Order Confirmation Form)" message should always take priority over any other "Order Placed" message types</li>
                        <li>If you have multiple "Order Placed" message types enabled, please see the <a href="#" @click.prevent="$emit('scroll-to-faq', 'orderPlacedMultipleFaq')">multiple "Order Placed" FAQ</a> for a more detailed explanation</li>            
                    </ul>
                </li>
                <li>This message should include a link to your customized Order Confirmation form where customers can review and confirm their order details
                    <ul>
                        <li>You must have an Order Confirmation form created and configured to use this message for the link to work</li>
                    </ul>
                </li>      
                <li>RedChirp should attempt to avoid sending this message when:
                    <ul>
                        <li>The phone number is not the bill to phone number for the order</li>
                        <li>The order total is less than $1.00</li>
                        <li>The phone number has received this message type within the last 7 days</li>
                    </ul>
                </li>
                <li>When enabled, this message should be triggered for all order types</li>
                <ul>
                    <li>There are default conditions set up to have relevant text for club orders and non-club orders</li>
                    <li>Keep this in mind when considering this message for club shipments; if needed, you can use conditions to exclude or change the text for club orders</li>
                    <li>Keep this in mind if you are creating orders for reservations / events; if needed, you can use conditions to exclude or change the text in this circumstance</li>
                </ul>
            </ul>
        </div>

        <div v-if="template_context === 'order-fulfilled-follow-up'">
            <strong>When will this message be sent</strong>

            <ul>
                <li>RedChirp will trigger this message when it has been the specified number of days between the first fulfillment date associated with this order</li>
                <ul>
                    <li>This message will only trigger one time per order</li>
                    <li>This message is sent to every phone number associated with the order</li>
                    <li>This message will not be sent if the order is cancelled</li>
                </ul>

                <li>Depending on how you use your POS / CRM, it is possible that your orders may not have fulfilment dates &mdash; as a result <strong>this message may not send for all orders</strong></li>
                <ul>
                    <li>To check the first fulfment dates of your orders go to Main Menu > [Your CRM Name, e.g. Commerce7, WineDirect, etc.] > Orders and look at the date in the first fulfillment column</li>
                    <li>If this column is blank no follow up message will be sent</li>
                    <li>You may need to do a re-sync of your CRM data for dates to populate &mdash;  RedChirp automatically triggers a full sync when you first turn this feature on, give it a day or so to complete</li>
                    <li>When checking your orders over, be sure to consider different types of orders: club orders, pickup orders, carry out orders and shipped orders may behave differently, depending on how you use your CRM</li>
                </ul>

                <li>This message is triggered for all order types</li>
                <ul>
                    <li>Keep this in mind when considering this message for club shipments; if needed, you can use conditions to exclude or change the text for club orders. </li>
                    <li>Keep this in mind if you are creating orders for reservations / events; if needed, you can use conditions to exclude or change the text in this circumstance.</li>
                </ul>
            </ul>
        </div>
    </div>
</template>

<script>
export default {
    name: 'OrderPlacedMessageInfo',

    props: {
        template_context: {
            type: String,
            required: true
        }
    }
}
</script>